import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { LogService } from '@services/util/log.service';
import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';

/**
 * Interface to track field changes in booking
 */
export interface FieldChange {
  fieldName: string;
  oldValue: any;
  newValue: any;
  tabName?: string; // Optional tab identifier
}

/**
 * Service to track changes made to booking data
 * Uses a BehaviorSubject to emit changes to all components
 */
@Injectable({
  providedIn: 'root'
})
export class BookingChangeTrackerService {
  // Original booking data (unmodified)
  private originalBooking: Partial<IBookingModel> = {};

  // BehaviorSubject to track changes
  private changesSubject = new BehaviorSubject<FieldChange[]>([]);
  public changes$ = this.changesSubject.asObservable();

  // Keep track of all changed fields
  private changedFields: Map<string, FieldChange> = new Map();

  // Flag indicating if there are any unsaved changes
  public hasUnsavedChanges = false;

  constructor(private readonly log: LogService) {}

  /**
   * Initialize the tracker with original booking data
   * @param booking The original booking data to track changes against
   */
  initialize(booking: IBookingModel): void {
    // Create a deep copy of the original booking
    this.originalBooking = this.deepCopy(booking);
    this.changedFields.clear();
    this.changesSubject.next([]);
    this.hasUnsavedChanges = false;
  }

  /**
   * Track changes in a specific field
   * @param fieldName The name of the field that changed
   * @param newValue The new value
   * @param tabName Optional tab name for field location
   */
  trackChange(fieldName: string, newValue: any, tabName?: string): void {
    const oldValue = this.getNestedProperty(this.originalBooking, fieldName);

    // Skip if values are the same
    if (this.areValuesEqual(oldValue, newValue)) {
      this.changedFields.delete(fieldName);
    } else {
      // Add to changed fields map
      this.changedFields.set(fieldName, {
        fieldName,
        oldValue,
        newValue,
        tabName
      });
    }

    this.hasUnsavedChanges = this.changedFields.size > 0;
    this.changesSubject.next(Array.from(this.changedFields.values()));
  }

  /**
   * Check if a field has been modified
   * @param fieldName The field name to check
   * @returns True if the field has been modified
   */
  isFieldChanged(fieldName: string): boolean {
    return this.changedFields.has(fieldName);
  }

  /**
   * Get only the changed fields as a partial booking object
   * @returns Partial booking with only changed fields
   */
  getChangedFieldsObject(): Partial<IBookingModel> {
    const changedObj: Partial<IBookingModel> = { id: this.originalBooking.id };

    this.changedFields.forEach((change) => {
      // Handle nested properties using dot notation (e.g., 'billing.address')
      if (change.fieldName.includes('.')) {
        const parts = change.fieldName.split('.');
        let current: any = changedObj;

        // Create nested object structure
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }

        // Set the value
        current[parts[parts.length - 1]] = change.newValue;
      } else {
        // Special handling for relation fields that need only IRI
        if (change.fieldName === 'gender' && change.newValue && typeof change.newValue === 'object') {
          // Check if we have an object with @id property
          if (change.newValue['@id']) {
            // Use just the IRI string as the API requires
            (changedObj as any)[change.fieldName] = change.newValue['@id'];
          }
          // Also check for a property called iri as used in our components
          else if (change.newValue.iri) {
            (changedObj as any)[change.fieldName] = change.newValue.iri;
          }
          // If we don't have @id or iri, keep the original object
          else {
            // No IRI found, use as-is
            (changedObj as any)[change.fieldName] = change.newValue;
          }
        } else {
          // Simple property assignment for non-relation fields
          (changedObj as any)[change.fieldName] = change.newValue;
        }
      }
    });

    this.log.debug('Final changed fields object:', changedObj);
    return changedObj;
  }

  /**
   * Reset the tracker after saving changes
   */
  resetAfterSave(updatedBooking: IBookingModel): void {
    this.initialize(updatedBooking);
  }

  /**
   * Get a copy of the original booking data
   * @returns A deep copy of the original booking data
   */
  getOriginalBooking(): IBookingModel {
    return this.deepCopy(this.originalBooking) as IBookingModel;
  }

  /**
   * Get number of changes by tab (for UI indicators)
   * @param tabName The tab name to count changes for
   * @returns Number of changes in that tab
   */
  getChangeCountByTab(tabName: string): number {
    return Array.from(this.changedFields.values()).filter((change) => change.tabName === tabName).length;
  }

  /**
   * Get total number of changes
   * @returns Total number of changed fields
   */
  getTotalChangeCount(): number {
    return this.changedFields.size;
  }

  /**
   * Compare two values for equality
   * Handles special cases like dates, arrays, and objects
   */
  private areValuesEqual(a: any, b: any): boolean {
    // Handle null/undefined
    if (a === b) return true;
    if (a === null || a === undefined || b === null || b === undefined) return false;

    // Simplified date comparison: convert any potential date to timestamp for comparison
    const isDateValue = (val: any): boolean => val instanceof Date || (typeof val === 'string' && !isNaN(Date.parse(val)));

    if (isDateValue(a) && isDateValue(b)) {
      try {
        const timeA = a instanceof Date ? a.getTime() : new Date(a).getTime();
        const timeB = b instanceof Date ? b.getTime() : new Date(b).getTime();
        return timeA === timeB;
      } catch {
        // If date parsing fails, fall through to other comparisons
      }
    }

    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!this.areValuesEqual(a[i], b[i])) return false;
      }
      return true;
    }

    // Handle objects (excluding arrays and dates)
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) return false;

      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!this.areValuesEqual(a[key], b[key])) return false;
      }

      return true;
    }

    // Simple value comparison
    return a === b;
  }

  /**
   * Deep copy an object to avoid reference issues
   */
  private deepCopy<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Get a nested property value using a string path
   * Example: getNestedProperty(obj, 'user.address.street')
   */
  private getNestedProperty(obj: any, path: string): any {
    if (!obj) return undefined;

    const parts = path.split('.');
    let current = obj;

    for (const part of parts) {
      if (current === null || current === undefined) return undefined;
      current = current[part];
    }

    return current;
  }
}
